import { z } from 'zod';

// Indicator value to allow checking if a given object is a placeholder. A Symbol would be nicer,
// but we can't do it since this value needs to be JSON serializable. Placeholders that are created
// during parse are going to be serialized as is since we don't use a transform step before
// serialization.
const unknownValuePlaceholderMarker = 'fa9fc083-53f0-4616-a4da-2ca792b8f4f8';

// Using a symbol key here since these are never JSON serialized. This ensures that the info added
// here is only a runtime thing. To access the fallback value use getValueWithPlaceholder.
const fallbackValueKey = Symbol('fallbackValueKey');

export interface UnknownValuePlaceholder<TFallback> {
  marker: typeof unknownValuePlaceholderMarker;
  unknownValueJson: string;
  [fallbackValueKey]: TFallback;
}

const schemaSerializedUnknownValuePlaceholder = z.object({
  marker: z.literal(unknownValuePlaceholderMarker),
  unknownValueJson: z.string(),
});

export type WithUnknownValuePlaceholder<TValue, TFallback = TValue> =
  | TValue
  | UnknownValuePlaceholder<TFallback>;

export type ValueOrPlaceholder<T> =
  T extends any ?
    // This maps across the constituents of the union due to the distributive property of conditional
    // types, allowing us to filter out the UnknownValuePlaceholder and replace ut by its fallback
    T extends UnknownValuePlaceholder<infer TFallback> ?
      TFallback
    : T
  : never;

export function isUnknownValuePlaceholder<TValue, TFallback = TValue>(
  value: TValue | UnknownValuePlaceholder<TFallback>,
): value is UnknownValuePlaceholder<TFallback> {
  // shortcut to avoid somewhat expensive parse
  if (typeof value !== 'object') {
    return false;
  }
  return schemaSerializedUnknownValuePlaceholder.safeParse(value).success;
}

export function getValueWithPlaceholder<TValue, TFallback = TValue>(
  value: TValue | UnknownValuePlaceholder<TFallback>,
): TValue | TFallback {
  if (isUnknownValuePlaceholder(value)) {
    return getPlaceholderValue<TValue, TFallback>(value);
  }
  return value;
}

export function getPlaceholderValue<TValue, TFallback = TValue>(
  value: UnknownValuePlaceholder<TFallback>,
): TValue | TFallback {
  return value[fallbackValueKey];
}

/**
 * Creates a schema for a union type that supports unknown values in a forward-compatible way.
 *
 * When serializing a union type (like an enum), we need to deal with the situation where a later
 * version adds another case to the union. Without special provision older versions just break since
 * they don't recognize the new enum case as a valid value.
 *
 * Wrapping enums in this placeholder works around that. When an enum schema fails to parse, it is
 * wrapped in a placeholder object. The original value is kept around inside the wrapper.
 * Additionally we also add a runtime only fallback value that should be used instead.
 *
 * On serialization the wrapper is written out as is. New clients that recognize the wrapped type
 * will unwrap it and restore the original value.
 */
export function makeUnknownValuePlaceholderSchema<
  TSchema extends z.ZodType,
  TFallback = z.infer<TSchema>,
>(schema: TSchema, fallbackValue: TFallback) {
  return z
    .union([
      schema,
      schemaSerializedUnknownValuePlaceholder.transform<
        z.infer<TSchema> | UnknownValuePlaceholder<TFallback>
      >((placeholder) => {
        try {
          const result = schema.safeParse(JSON.parse(placeholder.unknownValueJson));
          if (result.success) {
            return result.data;
          }
        } catch {
          // continue below with returning the placeholder as is
        }
        return { ...placeholder, [fallbackValueKey]: fallbackValue };
      }),
    ])
    .catch(
      (ctx: { input: unknown }): UnknownValuePlaceholder<TFallback> => ({
        marker: unknownValuePlaceholderMarker,
        // We store the original value as JSON serialized so we can deal with a consistent string type when parsing
        unknownValueJson: JSON.stringify(ctx.input),
        [fallbackValueKey]: fallbackValue,
      }),
    );
}
